import React, { ChangeEvent, Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { formatUserName } from "@formant/core/common/formatUserName";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { IComment } from "@formant/core/model/IComment";
import { IEventOrAggregate } from "@formant/core/model/IEventOrAggregate";
import { IFilter } from "@formant/core/model/IFilter";
import { ITags } from "@formant/core/model/ITags";
import { IValidation } from "@formant/core/model/IValidation";
import { appStoreBase, defaultStoreId } from "@formant/rectangle-sdk";
import { InputElement } from "@formant/ui/component/common/Field";
import { Icon } from "@formant/ui/component/common/Icon";
import { animate } from "@formant/ui/utils/animate";

import { track } from "../../analytics";
import {
    appStore,
    commentStore,
    deviceDetailsStore,
    deviceStore,
    eventStore,
    observeStore,
    userStore,
} from "../../stores";
import { formatDateTime } from "../../utils/formatDateTime";
import { Avatar } from "../common/Avatar";
import { Buttons } from "../common/Buttons";
import { Details } from "../common/Details";
import { EditTags } from "../common/EditTags";
import { Form } from "../common/Form";
import { Popup } from "../common/Popup";
import { StandardButton } from "../common/StandardButton";
import { StreamIcon } from "../common/StreamIcon";
import { TextareaField } from "../common/TextareaField";
import { ValidationMessages } from "../common/ValidationMessages";
import * as styles from "./EditComment.scss";

export interface IEditCommentProps {
    comment: IComment;
    onSelectEvent?: (event: IEventOrAggregate) => void;
    context: IFilter;
    filter?: IFilter;
}

@observer
export class EditComment extends Component<IEditCommentProps> {
    @observable
    private comment: IComment = this.props.comment;
    @observable
    private validation: IValidation = {};
    @observable
    private loading: boolean = false;
    @observable
    private visible: boolean = true;

    constructor(props: IEditCommentProps) {
        super(props);
    }

    public render() {
        const user = userStore.authenticated();
        if (!user) {
            return false;
        }
        const {
            comment,
            validation,
            onClose,
            onChangeMessage,
            onChangeTags,
            loading,
            onSubmit,
            onLoading,
        } = this;
        const message = comment?.message || "";
        const keys = Object.keys(comment?.tags || {});
        const tags = deviceStore.tags({ enabled: true, type: "default" }) || {};

        const { deviceId, streamName, streamType, time } = comment;
        const device = deviceId ? deviceDetailsStore.get(deviceId) : undefined;
        const deviceName = device ? device.name : "";

        return (
            <Popup
                visible={this.visible}
                className={styles["edit-comment"]}
                onClose={onClose}
            >
                <Form onSubmit={onSubmit} onLoading={onLoading}>
                    <div className={styles.header}>
                        <Avatar
                            userId={defined(user.id)}
                            size="tiny"
                            light={true}
                        />
                        <span className={styles.name}>
                            {formatUserName(user)}
                        </span>
                    </div>
                    <Details
                        className={styles.details}
                        entries={[
                            {
                                label: "Time",
                                content: (
                                    <div className={styles.detail}>
                                        <Icon name="clock" />
                                        {formatDateTime(parseIsoDate(time))}
                                    </div>
                                ),
                            },
                            {
                                label: "Device",
                                content: deviceId && (
                                    <div className={styles.detail}>
                                        <Icon name="device" />
                                        {deviceName}
                                    </div>
                                ),
                            },
                            {
                                label: "Stream",
                                content: (streamType || streamName) && (
                                    <div className={styles.detail}>
                                        {streamType && (
                                            <StreamIcon type={streamType} />
                                        )}
                                        {streamName}
                                    </div>
                                ),
                            },
                        ]}
                    />
                    <TextareaField
                        invalid={!!validation.message}
                        placeholder="Comment"
                        value={message}
                        onChange={onChangeMessage}
                        autoFocus={true}
                    />
                    <EditTags
                        className={styles.field}
                        value={comment?.tags || {}}
                        small={true}
                        onChange={onChangeTags}
                        invalid={!!this.validation.tags}
                        keys={keys}
                        tags={tags}
                    />
                    <ValidationMessages validation={this.validation} />
                    <Buttons className={styles.buttons}>
                        <StandardButton onClick={onClose} type="button">
                            Cancel
                        </StandardButton>
                        <StandardButton primary={true} loading={loading}>
                            Save
                        </StandardButton>
                    </Buttons>
                </Form>
            </Popup>
        );
    }

    private onClose = async () => {
        this.visible = false;
        await animate();
        observeStore.editingComment = undefined;
    };

    private onChangeMessage = (event: ChangeEvent<InputElement>) => {
        const { comment } = this;
        if (comment) {
            comment.message = event.target.value;
        }
    };

    private onChangeTags = (tags: ITags) => {
        const { comment } = this;
        if (comment) {
            comment.tags = tags;
        }
    };

    private onSubmit = async () => {
        this.validation = {};

        // local validation
        const { context, filter, onSelectEvent } = this.props;
        const contextTagKeys = new Set([
            ...Object.keys(context.tags || {}),
            ...Object.keys(filter?.tags || {}),
        ]);
        const intersection = new Set(
            Object.keys(this.comment.tags || {}).filter(_ =>
                contextTagKeys.has(_),
            ),
        );
        if (intersection.size) {
            this.validation = {
                tags: [
                    `Do not use tags used by this channel or filter: ${[
                        ...intersection,
                    ].join(", ")}`,
                ],
            };
            return;
        }

        try {
            const comment = await commentStore.save({
                ...this.comment,
                message: this.comment.message.trim(),
            });
            eventStore.clear(); // So new tags used in the comment are immediately available.
            const message = this.comment?.id
                ? "Comment edited"
                : "Comment created";
            appStore.setSnackbar(
                message,
                onSelectEvent
                    ? {
                          label: "View",
                          onClick: () => onSelectEvent({ event: comment }),
                      }
                    : undefined,
            );
            track(message);
            await this.onClose();
            observeStore.viewEvent(comment);
            const state = appStoreBase(defaultStoreId).getState();
            state.setEventSidebarIsOpen(true);
            state.setSelectedEventId(comment.id);
        } catch (error) {
            const responseError = narrowError(error, ResponseError);
            const { statusCode } = responseError;
            if (statusCode === 422) {
                this.validation = responseError.validationErrors;
            } else {
                throw error;
            }
        }
    };

    private onLoading = (loading: boolean) => {
        this.loading = loading;
    };
}
